import React from 'react';
import './PrivacyPolicyPage.css';

export const PrivacyPolicyPage = () => {
    const currentYear = new Date().getFullYear();
    const updateDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

    return (
        <div className="privacy-body">
            <div className="privacy-container">
                <h1>Snap Solver AI – Privacy Policy</h1>
                <div className="privacy-updated">Last updated: {updateDate}</div>

                <p>
                    Snap Solver AI respects your privacy and is designed to minimize data collection while providing on-demand assistance for learning and productivity.
                    This Privacy Policy explains what information is collected, how it is used, and how it is protected.
                </p>

                <h2>1. Information We Collect</h2>

                <p><strong>a. User-Provided Information</strong></p>
                <ul>
                    <li>Authentication tokens or session identifiers used to maintain login state</li>
                    <li>Optional model API key provided by the user via the web dashboard</li>
                </ul>

                <p><strong>b. On-Demand Content</strong></p>
                <p>
                    When the user explicitly activates the extension, a selected visible image region (question snapshot) may be temporarily sent to the backend for processing.
                </p>

                <p>We do <strong>not</strong> collect:</p>
                <ul>
                    <li>Browsing history</li>
                    <li>Full webpage content</li>
                    <li>Keystrokes</li>
                    <li>Personal identifiers</li>
                    <li>Background activity</li>
                </ul>

                <h2>2. How Information Is Used</h2>
                <p>Collected information is used only to:</p>
                <ul>
                    <li>Authenticate the user session</li>
                    <li>Process selected questions on demand</li>
                    <li>Generate an AI-powered response</li>
                    <li>Maintain basic configuration and preferences</li>
                </ul>

                <p>
                    No information is used for advertising, profiling, tracking, or analytics beyond basic operational metrics.
                </p>

                <h2>3. Data Storage & Retention</h2>
                <ul>
                    <li>Selected images and requests are processed in memory and are not permanently stored</li>
                    <li>Authentication tokens are stored locally only to maintain session state</li>
                    <li>API keys are stored securely on the backend in encrypted form and are never stored in the browser</li>
                </ul>

                <p>
                    User content is not retained after processing unless explicitly required for debugging or compliance.
                </p>

                <h2>4. Data Sharing &amp; Third Parties</h2>
                <p>
                    <strong>Data Processing Flow:</strong>
                </p>
                <p>When you capture a screenshot, here is exactly what happens:</p>
                <ol>
                    <li>The image is securely transmitted to our backend server (snapsolver.smarttechbros.com) via encrypted HTTPS connection</li>
                    <li>The server authenticates your request using your JWT token</li>
                    <li>The server forwards the image to Google Gemini's AI service for analysis</li>
                    <li>Gemini generates an explanation or answer</li>
                    <li>The response is returned to your browser</li>
                    <li>Screenshots are <strong>NOT permanently stored</strong> after processing</li>
                </ol>

                <p>
                    <strong>Third-Party Services:</strong>
                </p>
                <p>
                    We share selected content only with Google Gemini API solely for generating educational responses.
                    We do not sell, rent, or share data with advertisers or unrelated third parties.
                </p>

                <h2>5. Usage Monitoring &amp; Quota Tracking</h2>
                <p>
                    To ensure fair usage and prevent abuse, we track the following metrics:
                </p>
                <ul>
                    <li><strong>Daily request count:</strong> Number of times you use the extension per day (limit: 1000 requests/day)</li>
                    <li><strong>Per-minute request count:</strong> Number of requests within a 60-second window (limit: 15 requests/minute)</li>
                    <li><strong>Success/failure status:</strong> Whether requests succeeded or failed (for debugging purposes)</li>
                    <li><strong>Timestamp:</strong> When each request was made</li>
                </ul>
                <p>
                    <strong>Data Retention:</strong> Usage tracking data is automatically deleted after <strong>90 days</strong>.
                    This data is never used for advertising, profiling, or sold to third parties.
                </p>

                <h2>6. Cross-Device Synchronization</h2>
                <p>
                    When you are logged in, the extension periodically checks with our servers
                    (every <strong>30 seconds</strong>) to synchronize your settings across devices. This includes:
                </p>
                <ul>
                    <li>API key configuration status (whether a key is set or removed)</li>
                    <li>Logout events from other devices (to keep sessions synchronized)</li>
                    <li>Account settings updates</li>
                </ul>
                <p>
                    This polling ensures your preferences stay up-to-date across all your browsers and devices.
                    No browsing data or content is transmitted during these sync checks.
                </p>

                <h2>7. User Control</h2>
                <ul>
                    <li>Log out at any time</li>
                    <li>Remove or update your API key</li>
                    <li>Clear local extension data via browser settings</li>
                </ul>

                <p>
                    Clearing the API key immediately disables processing and removes access.
                </p>

                <h2>8. Security</h2>
                <ul>
                    <li>Encrypted transmission (HTTPS)</li>
                    <li>Encrypted storage of API keys on backend</li>
                    <li>No API keys stored permanently in the extension</li>
                    <li>No logging of sensitive content</li>
                </ul>

                <h2>9. Restricted Environments</h2>
                <p>
                    Snap Solver AI is intended for learning and self-study use only. It is not designed to bypass restricted testing environments or proctored systems.
                </p>

                <h2>10. Children’s Privacy</h2>
                <p>
                    Snap Solver AI is not directed to children under the age of 13 and does not knowingly collect personal information from children.
                </p>

                <h2>11. Changes to This Policy</h2>
                <p>
                    This policy may be updated from time to time. Any changes will be reflected here with an updated revision date.
                </p>

                <h2>12. Contact</h2>
                <p>
                    If you have questions about this Privacy Policy or data handling practices, please contact:
                </p>
                <ul>
                    <li>Email: <strong>support@smarttechbros.com</strong></li>
                    <li>Website: <strong><a href="https://snapsolverai.smarttechbros.com" target="_blank" rel="noopener noreferrer">https://snapsolverai.smarttechbros.com</a></strong></li>
                </ul>

                <div className="privacy-note">
                    This extension works only when explicitly activated by the user and does not perform background monitoring or automatic data collection.
                </div>

                <footer className="privacy-footer">
                    © <span id="year">{currentYear}</span> Snap Solver AI. All rights reserved.
                </footer>
            </div>
        </div>
    );
};
